import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import { appTheme } from '@/config/colors';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  FlatList,
  Linking,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Text } from '@/components/Text';
import api from '../../api/axios';
import { DigaMessages } from './DigaMessages';
import { styles } from './styles';

export function DigaCodes() {
  const [isModalVisible, setModalVisible] = useState(false);
  const [
    isDeactivationPostponeConsentModalVisible,
    setDeactivationPostponeConsentModalVisible,
  ] = useState(false);
  const [consentId, setConsentId] = useState(null);
  const [isModalErrorVisible, setModalErrorVisible] = useState(false);
  const [isModalErrorAPIVisible, setModalErrorAPIVisible] = useState(false);
  const [isModalNetworkErrorVisible, setModalNetworkErrorVisible] =
    useState(false);
  const [isScreenInputVisible, setScreenInputVisible] = useState(false);
  const [isScreenRedeemedInfoCodeVisible, setScreenRedeemedInfoCodeVisible] =
    useState(false);
  const [isScreenExpiredCodesVisible, setScreenExpiredCodesVisible] =
    useState(false);
  const [
    isScreenNoInternetConnectionVisible,
    setScreenNoInternetConnectionVisible,
  ] = useState(false);
  const [isNetworkError, setNetworkError] = useState(false);

  const germanDateFormat = 'DD.MM.YYYY';
  const email = 'info@neolexon.de';

  let [expiredCodesList, setExpiredCodesList] = useState([]);
  let [getActiveCode, setActiveCode] = useState([]);
  let [getExpirationDate, setExpirationDate] = useState([]);
  let [isValidated] = useState(false);

  const responsiveFontSize = useResponsiveFontSize(1);
  const getResponsiveFontSize = (factor) => {
    return responsiveFontSize * factor;
  };

  let responsiveStyles = StyleSheet.create({
    buttonStyle: {
      borderRadius: 8,
      backgroundColor: appTheme.primary,
      padding: getResponsiveFontSize(1),
    },
    buttonTitleStyle: {
      fontSize: getResponsiveFontSize(1.1),
      fontWeight: '700',
    },
  });

  const {
    control,
    clearErrors,
    handleSubmit,
    formState: { isSubmitted },
  } = useForm({
    mode: 'all',
    defaultValues: {
      digaCode: '',
    },
  });

  const resetErrorModalVisibility = () => {
    setModalErrorVisible(false);
    clearErrors();
  };

  const digaRegExPattern = /^[2-7a-zA-Z]{16}$/g;
  const digaCharacters = (value) => {
    const digaCodeRegEx = new RegExp(digaRegExPattern);
    isValidated = digaCodeRegEx.test(value);
    return isValidated ? isValidated : clearErrors(value);
  };

  const isCodeExpired = (dateToCheck) =>
    moment(new Date()).isAfter(dateToCheck);

  const extractExpiredCodes = (codesInfo) => {
    const expiredCodes = codesInfo.filter((codeInfo) =>
      isCodeExpired(codeInfo.expireAt),
    );
    setScreenExpiredCodesVisible(expiredCodes.length > 0);
    const list = expiredCodes.map((expiredValue) => {
      return {
        digaCode: expiredValue.digaCode,
        expireAt: moment(expiredValue.expireAt).format(germanDateFormat),
      };
    });
    setExpiredCodesList(list);
  };

  const initDigaCodeList = () => {
    api
      .get('/api/digacodes/list')
      .then((response) => response.data)
      .then((codesInfo) => {
        const activeCode = codesInfo.find((codeInfo) => codeInfo.isActive);
        if (activeCode) {
          setActiveCode(activeCode.digaCode);
          setExpirationDate(
            moment(activeCode.expireAt).format(germanDateFormat),
          );
          setScreenInputVisible(false);
          setScreenRedeemedInfoCodeVisible(true);
        } else {
          setScreenInputVisible(true);
          setScreenRedeemedInfoCodeVisible(false);
        }

        extractExpiredCodes(codesInfo);
      })
      .catch(() => {
        setNetworkError(true);
        setModalNetworkErrorVisible(true);
        setScreenNoInternetConnectionVisible(true);
      });
  };

  const verifyAndRedeemDigaCode = async (currentCode) => {
    try {
      const response = await api.post('/api/digacodes/verify', {
        digaCode: currentCode,
      });
      if (
        response.data &&
        response.data.deactivationPostponeConsent &&
        !response.data.deactivationPostponeConsent.givenConsent
      ) {
        setDeactivationPostponeConsentModalVisible(true);
        setConsentId(response.data.deactivationPostponeConsent._id);
      } else {
        setModalVisible(true);
      }
      initDigaCodeList();
    } catch (e) {
      if (e.toString() === 'Error: Network Error') {
        setModalNetworkErrorVisible(true);
      } else {
        setModalErrorAPIVisible(true);
      }
    }
  };

  const onSubmit = (submission) => {
    if (digaCharacters(submission.digaCode)) {
      !verifyAndRedeemDigaCode(submission.digaCode) &&
        setModalErrorAPIVisible(true);
    } else {
      setModalErrorVisible(true);
    }
  };

  const onRefreshScreen = () => {
    setNetworkError(false);
    initDigaCodeList();
  };

  useEffect(() => {
    initDigaCodeList();
  }, []);

  const contactInfoText =
    'Bei Fragen erreichen Sie uns am Telefon unter 089 24886 4440 ' +
    '\n' +
    'oder per E–Mail an ';

  const handleEmailPress = () => {
    Linking.openURL(`mailto:${email}`);
  };

  const getBorderStyle = function (digaCode) {
    const digaRegEx = new RegExp(digaRegExPattern);
    let isValid = digaRegEx.test(digaCode);

    if (isValid) {
      return styles.inputBorderAccepted;
    } else if ((isSubmitted || digaCode.length === 16) && !isValid) {
      return styles.inputBorderError;
    } else {
      return '';
    }
  };

  return (
    <SafeAreaView style={styles.fullHeight}>
      <ScrollView>
        <View style={styles.container}>
          {isScreenInputVisible ? (
            <View style={styles.container}>
              <Text style={styles.textInstructions}>
                Lösen Sie den Rezeptcode, den Sie von Ihrer Krankenkasse
                erhalten haben, hier ein:
              </Text>

              <View style={styles.inputContainer}>
                <Controller
                  control={control}
                  name="digaCode"
                  rules={{
                    required: true,
                    validate: (value) => digaCharacters(value),
                  }}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <>
                      <TextInput
                        autoCapitalize={'characters'}
                        autoComplete={'off'}
                        onBlur={onBlur}
                        onChangeText={(value) => onChange(value)}
                        maxLength={16}
                        style={[
                          styles.inputBox,
                          styles.inputText,
                          getBorderStyle(value),
                        ]}
                        value={value}
                      />
                    </>
                  )}
                />
                <Pressable
                  style={[
                    responsiveStyles.buttonStyle,
                    responsiveStyles.buttonTitleStyle,
                    { backgroundColor: appTheme.primary },
                  ]}
                  onPress={handleSubmit(onSubmit)}
                >
                  <Text className="text-xl text-bold text-center text-white">
                    einlösen
                  </Text>
                </Pressable>
              </View>
            </View>
          ) : null}

          {isScreenRedeemedInfoCodeVisible ? (
            <View style={styles.digaWrapper}>
              <Text style={styles.information}>
                {'Ihre aktuelle Lizenz endet am: '}
                <Text style={styles.bold}>
                  {getExpirationDate}
                  {'\n'}
                </Text>
                {'Verwendeter Rezeptcode: '}
                <Text style={styles.bold}>{getActiveCode}</Text>
              </Text>

              <Text style={styles.information}>
                {'Ihre Buchung verlängert sich '}
                <Text style={styles.bold}>nicht </Text>
                {'automatisch.' +
                  '\n' +
                  'Hier können Sie Ihre Lizenz verlängern, sobald diese abgelaufen ist.' +
                  '\n' +
                  'Hierzu benötigen Sie einen neuen Rezeptcode von Ihrer Krankenkasse.'}
              </Text>

              <Text style={[styles.information]}>
                {contactInfoText}
                <Text style={styles.email} onPress={handleEmailPress}>
                  {email}
                </Text>
              </Text>
            </View>
          ) : null}

          {isScreenExpiredCodesVisible ? (
            <View style={styles.listWrapper}>
              <Text style={styles.text}> Bisher eingelöste Rezeptcodes </Text>
              <FlatList
                data={expiredCodesList}
                renderItem={({ item }) => (
                  <>
                    <Text style={styles.digaCodeList}>
                      <Text style={styles.bold}>{item.digaCode}</Text>{' '}
                      (abgelaufen am {item.expireAt})
                    </Text>
                  </>
                )}
                keyExtractor={(item) => item.digaCode}
                scrollEnabled={false}
              />
            </View>
          ) : null}

          {isScreenNoInternetConnectionVisible && isNetworkError ? (
            <View style={styles.errorWrapper}>
              <Text style={styles.errorInternetHeader}>Fehler beim Laden</Text>
              <Text style={styles.errorInternetSubtitle}>
                Bitte überprüfen Sie Ihre Internetverbindung
              </Text>
              <PressableNeolexon onPress={onRefreshScreen}>
                <Text style={styles.errorInternetLink}>wiederholen</Text>
              </PressableNeolexon>
            </View>
          ) : null}

          <DigaMessages
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            isDeactivationPostponeConsentModalVisible={
              isDeactivationPostponeConsentModalVisible
            }
            setDeactivationPostponeConsentModalVisible={
              setDeactivationPostponeConsentModalVisible
            }
            isModalErrorVisible={isModalErrorVisible}
            setModalErrorVisible={resetErrorModalVisibility}
            isModalErrorAPIVisible={isModalErrorAPIVisible}
            setModalErrorAPIVisible={setModalErrorAPIVisible}
            isModalInternetErrorVisible={isModalNetworkErrorVisible}
            setModalInternetErrorVisible={setModalNetworkErrorVisible}
            consentId={consentId}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
